import { MutationTree } from 'vuex';
import { SingleFilter } from '@/store/types';
import { ProfileState } from './types';
import { ProfileResource, BrandResource, ProfileCategoryResource, CompanyResource} from '@/api/requests/profile/types';
import { ProfileMediaDeleteRequestPayload, ProfileMediaResourcePayload } from '@/store/modules/profile/types';

export const mutations: MutationTree<ProfileState> = {
    /** Profiles */

    /**
     * Add an profile to the list.
     *
     * @param state
     * @param profile
     */
    addProfile(state, profile: ProfileResource) {
        state.profiles.push(profile);
    },

    /**
     * Remove an profile from the list.
     *
     * @param state
     * @param profileId
     */
    removeProfile(state, profileId: number) {
        state.profiles = state.profiles.filter((profile) => profile.id !== profileId);
    },

    /**
     * Set the profiles list.
     *
     * @param state
     * @param profiles
     */
    setProfiles(state, profiles: ProfileResource[]) {
        state.profiles = profiles;
    },


    /**
     * Reset profiles
     *
     * @param state
     */
    resetProfiles(state) {
        state.profiles = [];
    },

    /**
     * Update an profile.
     *
     * @param state
     * @param profile
     */
    updateProfile(state, profile: ProfileResource) {
        const index = state.profiles.findIndex((item) => item.id === profile.id);
        state.profiles.splice(index, 1, profile);
    },

    /**
     * Set the active task id.
     *
     * @param state
     * @param id
     */
    setActiveProfileId(state, id: number) {
        state.activeProfileId = id;
    },

    /**
     * Update a profile.
     *
     * @param state
     * @param task
     */
    upsertProfile(state, profile: ProfileResource) {
        const index = state.profiles.findIndex((item) => item.id === profile.id);
        if (index > -1) {
            state.profiles.splice(index, 1, profile);
        } else {
            state.profiles.push(profile);
        }
    },

    /** Price Lists */

    /**
     * Add a brand to the list.
     *
     * @param state
     * @param brand
     */
    addBrand(state, brand: BrandResource) {
        state.brands.push(brand);
    },

    /**
     * Remove a brand from the list.
     *
     * @param state
     * @param profileId
     */
    removeBrand(state, brandId: number) {
        state.brands = state.brands.filter((brand) => brand.id !== brandId);
    },

    /**
     * Set the brand.
     *
     * @param state
     * @param brands
     */
    setBrands(state, brands: BrandResource[]) {
        state.brands = brands;
    },


    /**
     * Reset brands.
     *
     * @param state
     */
    resetBrands(state) {
        state.brands = [];
    },

    /**
     * Update a brand.
     *
     * @param state
     * @param brand
     */
    updateBrand(state, brand: BrandResource) {
        const index = state.brands.findIndex((item) => item.id === brand.id);
        state.brands.splice(index, 1, brand);
    },

    /**
     * Set the active brand id.
     *
     * @param state
     * @param id
     */
    setActiveBrandId(state, id: number) {
        state.activeBrandId = id;
    },

    /**
     * Update a brand.
     *
     * @param state
     * @param task
     */
    upsertBrand(state, brand: BrandResource) {
        const index = state.brands.findIndex((item) => item.id === brand.id);
        if (index > -1) {
            state.brands.splice(index, 1, brand);
        } else {
            state.brands.push(brand);
        }
    },

    /**
     * Set the active brand id.
     *
     * @param state
     * @param id
     */
    setProfilesBrand(state, {brand, profiles}) {
        brand.profiles = profiles;
    },

    /** Profile profileCategories */

    /**
     * Add a category to the list.
     *
     * @param state
     * @param category
     */
    addProfileCategory(state, category: ProfileCategoryResource) {
        state.profileCategories.push(category);
    },

    /**
     * Remove a category from the list.
     *
     * @param state
     * @param categoryId
     */
    removeProfileCategory(state, categoryId: number) {
        state.profileCategories = state.profileCategories.filter((category) => category.id !== categoryId);
    },

    /**
     * Set the profileCategories list.
     *
     * @param state
     * @param profileCategories
     */
    setProfileCategories(state, profileCategories: ProfileCategoryResource[]) {
        state.profileCategories = profileCategories;
    },


    /**
     * Reset profileCategories.
     *
     * @param state
     */
    resetProfileCategories(state) {
        state.profileCategories = [];
    },

    /**
     * Update a category.
     *
     * @param state
     * @param category
     */
    updateProfileCategory(state, category: ProfileCategoryResource) {
        const index = state.profileCategories.findIndex((item) => item.id === category.id);
        state.profileCategories.splice(index, 1, category);
    },

    /** Companies */

    /**
     * Add a company to the list.
     *
     * @param state
     * @param company
     */
    addCompany(state, company: CompanyResource) {
        const index = state.companies.findIndex((item) => item.id === company.id);
        if (index > -1) {
            state.companies.splice(index, 1, company);
        } else {
            state.companies.push(company);
        }
    },

    /**
     * Remove a company from the list.
     *
     * @param state
     * @param companyId
     */
    removeCompany(state, companyId) {
        state.companies = state.companies.filter((company) => company.id !== companyId);
    },

    /**
     * Reset the companies list.
     *
     * @param state
     */
    resetCompanies(state) {
        state.companies = [];
    },

    /**
     * Set the companies list.
     *
     * @param state
     * @param companies
     */
    setCompanies(state, companies) {
        state.companies = companies;
    },

    /** Filters */

    /**
     * Set a query filter in state.
     *
     * @param state
     * @param key
     * @param value
     */
    setFilter(state, { key, value }: SingleFilter) {
        state.filters = { ...state.filters, [key]: value };
    },

    /**
     * Reset a single filter.
     *
     * @param state
     * @param key
     */
    resetFilter(state, key: string) {
        delete state.filters[key];
    },

    /**
     * Reset query filters.
     *
     * @param state
     */
    resetFilters(state) {
        state.filters = {};
    },

    /**
     * Set selected profile.
     *
     * @param state
     * @param tasks
     */
    setSelectedProfile(state, profile: ProfileResource) {
        state.selectedProfile = profile;
    },

    /**
     * Add a media to a profile, or update if already exists.
     *
     * @param state
     * @param profileId
     * @param media
     */
    addMediaToProfile(state, { profileId, media }: ProfileMediaResourcePayload) {
        if (!state.selectedProfile) {
            return;
        }
        const index = state.selectedProfile && state.selectedProfile.media.length > 0
            ? state.selectedProfile.media.findIndex((item) => item.id === media.id)
            : -1;
        if (index > -1) {
            state.selectedProfile.media.splice(index, 1, media);
        } else {
            state.selectedProfile.media.push(media);
        }
    },

    /**
     * Remove a media from a profile.
     *
     * @param state
     * @param profileId
     * @param mediaId
     */
    removeMediaFromProfile(state, { profileId, mediaId }: ProfileMediaDeleteRequestPayload) {
        if (!state.selectedProfile) {
            return;
        }
        const index = state.selectedProfile && state.selectedProfile.media.length > 0
            ? state.selectedProfile.media.findIndex((item) => item.id === mediaId)
            : -1;
        if (index > -1) {
            state.selectedProfile.media.splice(index, 1);
        }
    },
};
